import { atom } from "jotai";

import { EventSignUpRow, UnknownGuestPrimaryChargePaymentMap } from "../types";
import { currentSignUpRowAtom } from "./eventSignUpProcess";
import { unknownGuestsAssignedToPayersAtom } from "./stores";

/**
 * Unknown Guest Related Actions
 */
export const hydrateUnknownGuestPlayerCountsAtom = atom(
  null,
  (get, set, payload: { signUpRow: EventSignUpRow }) => {
    const unknownGuestRegistrations = payload.signUpRow?.registrations.filter(
      (r) => !r.registrant.playerId && r.type === "GUEST"
    );

    const unknownGuestPrimaryChargeTypeIds = new Set();
    unknownGuestRegistrations.forEach((r) =>
      unknownGuestPrimaryChargeTypeIds.add(r.primaryRegistrationChargeTypeId)
    );

    const currentPaymentMap: UnknownGuestPrimaryChargePaymentMap[] = [];

    unknownGuestRegistrations.forEach((ugr) => {
      const chargeId = ugr.primaryRegistrationChargeTypeId;
      const payerId = ugr.payingMemberId;
      const matchingEntry = currentPaymentMap.find(
        (pm) => pm.payerId === payerId && pm.primaryChargeId === chargeId
      );
      if (matchingEntry) {
        matchingEntry.unknownGuestCount++;
      } else {
        currentPaymentMap.push({
          primaryChargeId: chargeId,
          payerId,
          unknownGuestCount: 1,
        });
      }
    });

    set(unknownGuestsAssignedToPayersAtom, currentPaymentMap);
  }
);

export const zeroOutUnknownGuestPaymentsForPayer = atom(
  null,
  (get, set, payload: { payerId: string }) => {
    const currentUnknownGuestAssignments = get(
      unknownGuestsAssignedToPayersAtom
    );
    const newAssignments = currentUnknownGuestAssignments.map((a) => {
      const isMatch = a.payerId === payload.payerId;
      return { ...a, unknownGuestCount: isMatch ? 0 : a.unknownGuestCount };
    });
    set(unknownGuestsAssignedToPayersAtom, newAssignments);
  }
);

export const setNumberOfUnknownGuestsMemberWillPayForAtom = atom(
  null,
  (get, set, payload: UnknownGuestPrimaryChargePaymentMap) => {
    const currentUnknownGuestAssignments = get(
      unknownGuestsAssignedToPayersAtom
    );
    //Remove any existing entry for this paying member and charge id combo and just replace it with the new values
    const newUnknownGuestAssignments = currentUnknownGuestAssignments.filter(
      (a) =>
        !(
          a.payerId === payload.payerId &&
          payload.primaryChargeId === a.primaryChargeId
        )
    );

    set(unknownGuestsAssignedToPayersAtom, [
      ...newUnknownGuestAssignments,
      {
        ...payload,
      },
    ]);
  }
);

/*
 * Handle progressing from 'who pays' step and reconciling all of the
 * unknown guests onto valid paying members.
 */
export const reconcileSignUpsWithUnknownGuestCountsAtom = atom(
  null,
  (get, set) => {
    const unknownGuestPayerCounts = get(unknownGuestsAssignedToPayersAtom);
    const currentSignUpRow = get(currentSignUpRowAtom);

    const unknownGuestAssignmentCountDown = [...unknownGuestPayerCounts];

    const unknownGuestPlayerRegistrations =
      currentSignUpRow?.registrations.filter(
        (registration) =>
          !registration.registrant.playerId && registration.type === "GUEST"
      ) || [];

    const unknownGuestRegistrationsWithCorrectPayerIds =
      unknownGuestPlayerRegistrations.flatMap((item, index) => {
        const payerIdToUse = unknownGuestAssignmentCountDown.find(
          (payerCount) => {
            const matchesCharge =
              payerCount.primaryChargeId ===
              item.primaryRegistrationChargeTypeId;
            const hasRoom = payerCount.unknownGuestCount > 0;
            if (matchesCharge && hasRoom) {
              payerCount.unknownGuestCount--;
              return true;
            }
            return false;
          }
        );
        if (payerIdToUse?.payerId) {
          return { ...item, payingMemberId: payerIdToUse.payerId };
        }
        return [];
      });

    const nonUnknownGuestRegistrations =
      currentSignUpRow?.registrations.filter(
        (r) => r.registrant.playerId || r.type !== "GUEST"
      ) ?? [];
    const allRegistrations = [
      ...nonUnknownGuestRegistrations,
      ...unknownGuestRegistrationsWithCorrectPayerIds,
    ];

    const newSignUpRow: EventSignUpRow | null = currentSignUpRow
      ? {
          ...currentSignUpRow,
          registrations: allRegistrations,
        }
      : null;
    set(currentSignUpRowAtom, newSignUpRow);
    return newSignUpRow;
  }
);
